import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEO from '../../components/SEO';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    interest: 'distribution',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (submitStatus !== 'idle') {
      const timer = setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d50ql25pfatkmn6if7v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          interest: 'distribution',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://trigreensoul.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "TRIGREENSOUL PRIVATE CUSTOM SERVICES, S.L.",
      "url": siteUrl,
      "logo": "https://public.readdy.ai/ai/img_res/ae0a5222-1684-471a-9ad4-134e3508a5e0.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Business Inquiries",
        "email": "info@trigreensoul.com",
        "availableLanguage": ["English", "Spanish"],
        "areaServed": "Europe"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Barcelona",
        "addressRegion": "Catalonia",
        "addressCountry": "ES"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "41.3874",
        "longitude": "2.1686"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us - TRIGREENSOUL | Partner With Europe's Leading Organic Distributor"
        description="Ready to partner with Europe's leading organic distributor? Get in touch with our team to discuss how we can support your business with premium organic produce and expert services. Barcelona, Spain."
        keywords="contact organic distributor, Barcelona organic supplier, organic produce inquiry, organic partnership, European organic trade contact"
        canonical={`${siteUrl}/contact`}
        schema={schema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/ae0a5222-1684-471a-9ad4-134e3508a5e0.png" alt="TRIGREENSOUL" className="h-12 w-auto" />
            <span className="text-xl font-semibold text-gray-900">TRIGREENSOUL</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">About</Link>
            <Link to="/products" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Products</Link>
            <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Services</Link>
            <Link to="/sustainability" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Sustainability</Link>
            <Link to="/contact" className="px-6 py-2 rounded-full border-2 border-emerald-700 text-emerald-700 bg-emerald-700 text-white transition-all whitespace-nowrap cursor-pointer">Partner With Us</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <span className="text-sm text-emerald-700 uppercase tracking-wider font-semibold">Contact Us</span>
            <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mt-4 mb-8 leading-tight">
              Let's Grow<br />Together
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Ready to partner with Europe's leading organic distributor? Get in touch with our team to discuss how we can support your business with premium organic produce and expert services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact_form">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all"
                    placeholder="Your Company Ltd."
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all"
                      placeholder="+34 XXX XXX XXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                      Country *
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all"
                      placeholder="Spain"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-semibold text-gray-700 mb-2">
                    Area of Interest *
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="distribution">Product Distribution</option>
                    <option value="international">International Trade</option>
                    <option value="consulting">Organic Crop Consulting</option>
                    <option value="partnership">General Partnership</option>
                    <option value="other">Other Inquiry</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your business needs and how we can help..."
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-emerald-700 text-white font-bold rounded-lg hover:bg-emerald-800 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message →'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg animate-fadeInUp">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-full animate-scaleIn">
                        <i className="ri-check-line text-xl text-white"></i>
                      </div>
                      <p className="text-emerald-800 font-semibold">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-fadeInUp">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-600 rounded-full animate-scaleIn">
                        <i className="ri-close-line text-xl text-white"></i>
                      </div>
                      <p className="text-red-800 font-semibold">Sorry, there was an error sending your message. Please try again.</p>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Get in Touch</h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-emerald-100 rounded-lg">
                    <i className="ri-map-pin-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Headquarters</h3>
                    <p className="text-gray-600 leading-relaxed">
                      TRIGREENSOUL PRIVATE CUSTOM SERVICES, S.L.<br />
                      Barcelona, Spain<br />
                      European Union<br />
                      CIF: B55411128
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-emerald-100 rounded-lg">
                    <i className="ri-mail-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
                    <p className="text-gray-600">
                      <a href="mailto:info@trigreensoul.com" className="hover:text-emerald-700 transition-colors">
                        info@trigreensoul.com
                      </a>
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-emerald-100 rounded-lg">
                    <i className="ri-time-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Business Hours</h3>
                    <p className="text-gray-600">
                      Monday - Friday: 9:00 - 18:00 CET<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Partner With Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-emerald-700 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">100% certified organic products</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-emerald-700 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Pan-European distribution network</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-emerald-700 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Expert organic crop consulting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-emerald-700 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Dedicated account management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-emerald-700 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Competitive pricing and flexible terms</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Visit Our Headquarters</h2>
            <p className="text-xl text-gray-600">
              Located in the heart of Barcelona, Spain
            </p>
          </div>
          <div className="relative h-[500px] w-full rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95777.49789922833!2d2.0784069!3d41.3873974!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a49816718e30e5%3A0x44b0fb3d4f47660a!2sBarcelona%2C%20Spain!5e0!3m2!1sen!2s!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="TRIGREENSOUL Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-12 mx-8 mt-16 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div>
              <h3 className="text-4xl font-light mb-4">Stay Connected</h3>
              <p className="text-gray-400 mb-8">Subscribe to organic industry insights</p>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Quick Links</h4>
              <div className="space-y-4">
                <Link to="/about" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">About Us</Link>
                <Link to="/products" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Products</Link>
                <Link to="/sustainability" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Certifications</Link>
                <Link to="/contact" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Contact</h4>
              <p className="text-white mb-8">info@trigreensoul.com</p>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-4">Headquarters</h4>
              <p className="text-white text-sm leading-relaxed">
                Barcelona, Spain<br />
                European Union
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 TRIGREENSOUL. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer text-sm">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
